import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { observe } from '~/services/observability/observe'
import { LoginByEmailErrorResponse, LoginByEmailResponse } from '~/types'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'

interface UserCredentials {
  username: string
  password: string
}

/**This query returns a refetch function which, when provided with a username/password, will invoke the query
 * return the results of the password login.
 * This call will not be cached, will not be retried automatically, and will not be refetched automatically on window focus.
 */
export const useLoginWithPassword = (
  onQuerySuccess:
    ((data: LoginByEmailResponse) => void) | undefined = undefined,
  options: Record<string, unknown> = {},
) => {
  const mangoApi = useMangoApi()
  const [userCredentials, setUserCredentials] = useState<
    UserCredentials | undefined
  >()
  const [loginErrorCleared, setLoginErrorCleared] = useState(false)

  const refetch = (username: string, password: string) => {
    setLoginErrorCleared(true)
    setUserCredentials({ username, password })
  }

  const clearLoginError = () => {
    setLoginErrorCleared(true)
    setUserCredentials(undefined)
  }

  const query = useQuery<
    LoginByEmailResponse | undefined,
    LoginByEmailErrorResponse
  >({
    queryKey: ['loginWithPassword'],
    queryFn: async () => {
      if (!userCredentials) return Promise.resolve(undefined)

      const result = await mangoApi.loginByEmail({ ...userCredentials })
      const { ok, data } = result
      if (!ok) {
        const loginError = data as LoginByEmailErrorResponse
        observe.track(
          AnalyticsNamespace.AUTHENTICATION,
          AuthenticationAnalyticsEvents.LoginFailed,
          {
            reasonCode: loginError?.code ?? 'unknown',
            status: loginError?.status,
          },
        )
        return Promise.reject(loginError)
      }

      const tokenData = data as LoginByEmailResponse

      observe.track(
        AnalyticsNamespace.AUTHENTICATION,
        AuthenticationAnalyticsEvents.LoginSucceeded,
      )
      onQuerySuccess?.(tokenData)
      return tokenData
    },
    ...options,
    enabled: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  })

  useEffect(() => {
    if (userCredentials) {
      query.refetch()
    }
  }, [userCredentials])

  useEffect(() => {
    if (query.isError) {
      setLoginErrorCleared(false)
    }
  }, [query.errorUpdatedAt, query.isError])

  return {
    ...query,
    error: loginErrorCleared ? null : query.error,
    isError: loginErrorCleared ? false : query.isError,
    refetch: refetch,
    clearLoginError,
  }
}
